import { useQuery } from "@tanstack/react-query";
import { useNavigate, generatePath } from "react-router-dom";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Card, CardContent, Chip, Stack,
  useMediaQuery, useTheme,
} from "@mui/material";
import { getAdminDisputes } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import useListQueryState from "../../../hooks/useListQueryState";

const formatCurrency = (cents, currency) =>
  currency ? `${currency} ${(cents / 100).toFixed(2)}` : `${(cents / 100).toFixed(2)}`;

const resolutionColor = { FULFILLED: "success", REFUNDED: "info", REOPENED: "default" };

const DisputeStageChip = ({ order }) => (
  <Chip
    label={order.dispute?.previousStatus || "—"}
    size="small"
    color={order.dispute?.previousStatus && order.dispute.previousStatus !== "DELIVERED" ? "warning" : "default"}
  />
);

const DisputeCards = ({ orders, onRowClick, showResolution }) => (
  <Stack spacing={2}>
    {orders.map((order) => (
      <Card key={order._id} variant="outlined" onClick={() => onRowClick(order._id)} sx={{ cursor: "pointer" }}>
        <CardContent>
          <Typography fontWeight={600}>{order.orderNumber}</Typography>
          <Typography variant="body2">Buyer: {order.user?.name || "—"}</Typography>
          <Typography variant="body2">Shop: {order.shop?.shopName || "—"}</Typography>
          <Typography variant="body2">Reason: {order.dispute?.reason?.replace(/_/g, " ")}</Typography>
          <Typography variant="body2">
            Stage: {order.dispute?.previousStatus || "—"}
            {order.dispute?.previousStatus && order.dispute.previousStatus !== "DELIVERED" && (
              <Chip label="pre-delivery" color="warning" size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
          {showResolution && (
            <Typography variant="body2">
              Resolved: <Chip label={order.status} size="small" color={resolutionColor[order.status] || "default"} />
              {order.dispute?.resolvedAt && ` · ${new Date(order.dispute.resolvedAt).toLocaleDateString()}`}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Raised: {order.dispute?.createdAt ? new Date(order.dispute.createdAt).toLocaleDateString() : "—"}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatCurrency(order.subtotalCents, order.currency)}
          </Typography>
        </CardContent>
      </Card>
    ))}
  </Stack>
);

const DisputeTable = ({ orders, onRowClick, showResolution }) => (
  <Table>
    <TableHead>
      <TableRow>
        <TableCell>Order #</TableCell>
        <TableCell>Buyer</TableCell>
        <TableCell>Shop</TableCell>
        <TableCell>Reason</TableCell>
        <TableCell>Stage</TableCell>
        <TableCell>Raised By</TableCell>
        <TableCell>Date</TableCell>
        {showResolution && <TableCell>Resolution</TableCell>}
        <TableCell>Total</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {orders.map((order) => (
        <TableRow key={order._id} hover onClick={() => onRowClick(order._id)} sx={{ cursor: "pointer" }}>
          <TableCell>{order.orderNumber}</TableCell>
          <TableCell>{order.user?.name || "—"}</TableCell>
          <TableCell>{order.shop?.shopName || "—"}</TableCell>
          <TableCell>
            <Chip label={order.dispute?.reason?.replace(/_/g, " ") || "—"} size="small" />
          </TableCell>
          <TableCell>
            <DisputeStageChip order={order} />
          </TableCell>
          <TableCell>{order.dispute?.raisedBy || "USER"}</TableCell>
          <TableCell>
            {order.dispute?.createdAt ? new Date(order.dispute.createdAt).toLocaleDateString() : "—"}
          </TableCell>
          {showResolution && (
            <TableCell>
              <Chip label={order.status} size="small" color={resolutionColor[order.status] || "default"} />
            </TableCell>
          )}
          <TableCell>{formatCurrency(order.subtotalCents, order.currency)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

// The open/closed sections paginate independently on the same page, so each
// gets its own URL param ("openPage" / "closedPage") rather than sharing
// "page" — otherwise paging one section would collide with the other's.
const DisputeSection = ({ title, resolved, isMobile, onRowClick, pageParam }) => {
  const [searchParams, updateParams] = useListQueryState();
  const page = Number(searchParams.get(pageParam)) || 0;
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-disputes", resolved, page],
    queryFn: () => getAdminDisputes({ page: page + 1, limit, resolved }),
  });

  const orders = data?.orders || [];

  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight={600} mb={2}>
        {title}
      </Typography>

      {isLoading ? (
        <CircularProgress />
      ) : orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None to show.
        </Typography>
      ) : (
        <>
          {isMobile ? (
            <DisputeCards orders={orders} onRowClick={onRowClick} showResolution={resolved} />
          ) : (
            <DisputeTable orders={orders} onRowClick={onRowClick} showResolution={resolved} />
          )}
          <TablePagination
            component="div"
            count={data?.total || 0}
            page={page}
            onPageChange={(_, p) => updateParams({ [pageParam]: p || undefined })}
            rowsPerPage={limit}
            rowsPerPageOptions={[limit]}
          />
        </>
      )}
    </Box>
  );
};

const DisputeList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleRowClick = (orderId) =>
    navigate(generatePath(frontendRoutes.ADMIN_DISPUTE_DETAIL, { orderId }));

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Disputes
      </Typography>

      <DisputeSection title="Open Disputes" resolved={false} isMobile={isMobile} onRowClick={handleRowClick} pageParam="openPage" />
      <DisputeSection title="Closed Disputes" resolved={true} isMobile={isMobile} onRowClick={handleRowClick} pageParam="closedPage" />
    </Box>
  );
};

export default DisputeList;
